import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import MetricImperialToggle from '@components/MetricImperialToggle';
import { ErrorMessageConstant } from '@utility/constants/common';
import { CommonUtility } from '@utility/common';
import { ToastMessage } from '@utility/toast';
import { LoaderBar } from '@components/LoaderBar';
import ItemService from '@utility/services/item';
import * as yup from 'yup';
import { FlexRow, JustifyBetweenRow, MuteText } from '@elements/Common';
import { DeleteConfirmWithButton } from '@components/DeleteConfirm';
import { AppModal } from '@elements/Modal';
import { MaskedNumberFormField } from '@elements/FormField';
import { useComponentsMetadata } from '@hooks/components';
import { ConversionUtility } from '@utility/conversions';
import {
  AddEditAttributeFormElement,
  AttributeSchema,
  convertFieldsToImperial,
  convertFieldsToMetric,
} from './FormElement';

const FormSchema = yup.object().shape({
  ...AttributeSchema,
  quantity: yup
    .number()
    .transform((v, o) => (o === '' ? null : v))
    .when('$canAddQuantity', (canAddQuantity, schema) =>
      canAddQuantity
        ? schema
            .typeError('Quantity is required field')
            .positive(0, 'Quantity must be greater than 0')
            .required('Quantity is required field')
            .when('units_per_carton', (units_per_carton, schema) =>
              schema.test(
                'quantity',
                'Should be greater than Unit Per Carton',
                quantity => {
                  if (!isNaN(units_per_carton) && !isNaN(units_per_carton)) {
                    return units_per_carton <= quantity;
                  }
                  return true;
                },
              ),
            )
        : schema.nullable(true),
    ),
});

export function AddEditAttributePopup({
  open,
  imperial,
  closeModal,
  currentAlias,
  itemId,
  itemUPC,
  refetch,
  skipSave,
  attributes,
  canAddQuantity,
  addByTextbox,
  fetchAttribute,
  hideAction,
  extraContent,
  minUnitPerCarton,
  maxUnitPerCarton,
  allowAdditions = true,
}) {
  const { options: optionsComponentsMetadata } = useComponentsMetadata();
  const [imperialForm, setImperialForm] = useState(false);
  const [processing, setProcessing] = useState('');
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [popupAttributes, setPopupAttributes] = useState([]);

  const { weightText, heightText } = useMemo(
    () => ({
      heightText: imperialForm ? 'inch' : 'cm',
      weightText: imperialForm ? 'lb' : 'kg',
      heightTooltipText: imperialForm ? 'inches' : 'cm',
    }),
    [imperialForm],
  );

  const {
    formState: { errors },
    handleSubmit,
    reset,
    control,
    getValues,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(FormSchema),
    shouldUnregister: true,
    mode: 'onChange',
    context: {
      maxUnitPerCarton,
      minUnitPerCarton,
      canAddQuantity,
    },
  });

  // @ts-expect-error - TS2769: No overload matches this call.
  const carton_gross_weight = watch('carton_gross_weight');
  // @ts-expect-error - TS2769: No overload matches this call.
  const carton_net_weight = watch('carton_net_weight');
  // @ts-expect-error - TS2769: No overload matches this call.
  const aliasName = watch('alias');

  const { isDuplicate, canTakeAction, isOld } = useMemo(
    () => ({
      isDuplicate:
        addByTextbox &&
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        (popupAttributes || []).some(x => x.alias === aliasName),
      canTakeAction:
        !hideAction &&
        aliasName &&
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        !(popupAttributes || []).find(x => x.alias === aliasName)?.primary,
      isOld:
        !hideAction &&
        aliasName &&
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        (popupAttributes || []).some(x => x.alias === aliasName),
    }),
    [addByTextbox, popupAttributes, aliasName, hideAction],
  );

  useEffect(() => {
    if (
      CommonUtility.isNotEmpty(carton_net_weight) &&
      CommonUtility.isNotEmpty(carton_gross_weight) &&
      // @ts-expect-error - TS2345: Argument of type 'readonly never[]' is not assignable to parameter of type 'numb...
      !isNaN(carton_net_weight) &&
      // @ts-expect-error - TS2345: Argument of type 'readonly never[]' is not assignable to parameter of type 'numb...
      !isNaN(carton_gross_weight)
    ) {
      // @ts-expect-error - TS2345: Argument of type '"carton_net_weight"' is not assignable to parameter of type 'n...
      trigger('carton_net_weight');
    }
  }, [carton_gross_weight]);

  useEffect(() => {
    const setAttribute = async () => {
      setImperialForm(imperial);
      let temp = [...attributes];
      if (fetchAttribute && !currentAlias && (attributes || []).length > 0) {
        try {
          setProcessing('Loading');
          const result = await ItemService.getItemAttribute(itemId);
          temp = [...(result?.data?.attributes || [])];
        } finally {
          setProcessing('');
        }
      }
      // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
      setPopupAttributes([...temp]);
      const options = temp.map(option => ({
        value: option.alias,
        text: option.alias,
      }));
      // @ts-expect-error - TS2345: Argument of type '{ value: any; text: any; }[]' is not assignable to parameter o...
      setDropdownOptions(options);

      if (currentAlias) {
        setFormData(
          temp.find(x => x.alias === currentAlias),
          imperial,
        );
      }
    };

    if (open) {
      setAttribute();
    } else {
      reset({});
      setDropdownOptions([]);
      setPopupAttributes([]);
    }
  }, [open, currentAlias, imperial, attributes, fetchAttribute]);

  function refactorNumber(value) {
    if (CommonUtility.isNotEmpty(value)) {
      return CommonUtility.roundNumber(value, 5);
    }
    return '';
  }

  const setFormData = (attribute, imperial) => {
    let temp = {};
    if (imperial) {
      temp = ConversionUtility.convertItemToImperial(attribute);
    } else {
      temp = ConversionUtility.convertItemToMetric(attribute);
    }
    const formValues = {
      // @ts-expect-error - TS2339: Property 'carton_gross_weight' does not exist on type '{}'.
      carton_gross_weight: refactorNumber(temp.carton_gross_weight?.value),
      // @ts-expect-error - TS2339: Property 'carton_height' does not exist on type '{}'.
      carton_height: refactorNumber(temp.carton_height?.value),
      // @ts-expect-error - TS2339: Property 'carton_length' does not exist on type '{}'.
      carton_length: refactorNumber(temp.carton_length?.value),
      // @ts-expect-error - TS2339: Property 'carton_net_weight' does not exist on type '{}'.
      carton_net_weight: refactorNumber(temp.carton_net_weight?.value),
      // @ts-expect-error - TS2339: Property 'carton_width' does not exist on type '{}'.
      carton_width: refactorNumber(temp.carton_width?.value),
      // @ts-expect-error - TS2339: Property 'units_per_carton' does not exist on type '{}'.
      units_per_carton: temp.units_per_carton,
      // @ts-expect-error - TS2339: Property 'alias' does not exist on type '{}'.
      alias: temp.alias,
    };
    // @ts-expect-error - TS2345: Argument of type '{ carton_gross_weight: string | number; carton_height: string ...
    reset({
      ...formValues,
    });
  };

  const save = async formData => {
    if (isDuplicate) {
      return;
    }
    setProcessing('Saving');
    const attribute = {
      carton_gross_weight: {
        value: formData.carton_gross_weight,
        unit: weightText,
      },
      carton_height: {
        value: formData.carton_height,
        unit: heightText,
      },
      carton_length: {
        value: formData.carton_length,
        unit: heightText,
      },
      carton_net_weight: {
        value: formData.carton_net_weight,
        unit: weightText,
      },
      carton_width: {
        value: formData.carton_width,
        unit: heightText,
      },
      units_per_carton: formData.units_per_carton,
      alias: formData.alias,
    };

    const reqData = {
      // @ts-expect-error - TS2345: Argument of type '{ carton_gross_weight: { value: any; unit: string; }; carton_h...
      attributes: [ConversionUtility.convertItemToMetric(attribute)],
      item_upc: itemUPC,
    };

    try {
      if (!skipSave) {
        await ItemService.saveAttributes(reqData);

        ToastMessage.success('Carton Configuration updated successfully.');
      }
      if (refetch) {
        await refetch(attribute, formData.quantity);
      }
      let convertedAttribute = attribute;
      if (imperial) {
        // @ts-expect-error - TS2740: Type 'AttributeMap' is missing the following properties from type '{ carton_gros...
        convertedAttribute = ConversionUtility.convertItemToImperial(attribute);
      } else {
        // @ts-expect-error - TS2740: Type 'AttributeMap' is missing the following properties from type '{ carton_gros...
        convertedAttribute = ConversionUtility.convertItemToMetric(attribute);
      }
      closeModal(convertedAttribute);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setProcessing('');
    }
  };

  const changeUnits = () => {
    const watchFields = getValues();
    let newFormData;
    if (imperialForm) {
      newFormData = convertFieldsToMetric(
        watchFields,
        optionsComponentsMetadata,
      );
    } else {
      newFormData = convertFieldsToImperial(
        watchFields,
        optionsComponentsMetadata,
      );
    }
    reset(newFormData);
    setImperialForm(!imperialForm);
  };

  const modalClose = () => {
    closeModal();
  };

  const aliasDropdownChanges = ({ value }) => {
    if (value.trim()) {
      // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
      const tempAttribute = popupAttributes.find(x => x.alias === value);
      if (tempAttribute) {
        setFormData(tempAttribute, imperialForm);
      } else {
        setDropdownOptions([
          // @ts-expect-error - TS2322: Type '{ key: any; value: any; text: any; item: {}; }' is not assignable to type ...
          ...dropdownOptions,
          // @ts-expect-error - TS2322: Type '{ key: any; value: any; text: any; item: {}; }' is not assignable to type ...
          {
            key: value,
            value,
            text: value,
            item: {},
          },
        ]);
        setFormData(
          {
            carton_gross_weight: {
              value: '',
              unit: weightText,
            },
            carton_height: {
              value: '',
              unit: heightText,
            },
            carton_length: {
              value: '',
              unit: heightText,
            },
            carton_net_weight: {
              value: '',
              unit: weightText,
            },
            carton_width: {
              value: '',
              unit: heightText,
            },
            units_per_carton: '',
            alias: value,
          },
          imperialForm,
        );
      }
    }
  };

  const markAsPrimary = async () => {
    try {
      setProcessing('Updating');
      await ItemService.itemAliasMarkPrimary(aliasName, {
        item_upc: itemUPC,
        primary: true,
      });
      if (refetch) {
        await refetch();
      }
      modalClose();
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setProcessing('');
    }
  };

  const deleteAttribute = async () => {
    try {
      setProcessing('Deleting');
      await ItemService.deleteItemAttribute(itemId, aliasName);

      ToastMessage.success('Item Carton Configuration deleted successfully.');
      if (refetch) {
        await refetch();
      }
      modalClose();
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setProcessing('');
    }
  };

  return (
    <AppModal
      open={open}
      closeModal={modalClose}
      size="tiny"
      title={`${currentAlias ? 'Edit' : 'Add'} ${aliasName}`}
    >
      {processing && <LoaderBar content={processing} />}
      <MetricImperialToggle
        toggle
        onChange={() => changeUnits()}
        checked={imperialForm}
        className="sk:mx-3.5!"
        wrapperClassName="sk:mt-2! sk:mb-3.5!"
      />
      <form noValidate onSubmit={handleSubmit(save)}>
        {extraContent}
        {/* @ts-expect-error - TS2739: Type '{ addByTextbox: any; aliasDropdownChanges: ({ value }:... */}
        <AddEditAttributeFormElement
          addByTextbox={addByTextbox}
          aliasDropdownChanges={aliasDropdownChanges}
          control={control}
          currentAlias={currentAlias}
          dropdownOptions={dropdownOptions}
          errors={errors}
          imperialForm={imperialForm}
          isDuplicate={isDuplicate}
          allowAdditions={allowAdditions}
        />
        {canAddQuantity && (
          <div className="sk:flex sk:flex-wrap sk:-mx-4">
            <div className="sk:min-w-0 sk:flex-1 sk:px-4">
              <MaskedNumberFormField
                error={errors.quantity}
                hint={errors.quantity?.message}
                label={<MuteText>Quantity</MuteText>}
                placeholder="Quantity"
                name="quantity"
                control={control}
                defaultValue=""
              />
            </div>
          </div>
        )}
        <JustifyBetweenRow
          className={canTakeAction && isOld ? '' : 'sk:justify-end! sk:mt-2!'}
        >
          {canTakeAction && isOld && (
            <FlexRow>
              <HoverBorderButton onClick={markAsPrimary} type="button">
                Mark as Primary
              </HoverBorderButton>
              <DeleteConfirmWithButton onConfirm={deleteAttribute} />
            </FlexRow>
          )}
          <FlexRow>
            <HoverBorderButton onClick={modalClose} type="button">
              Cancel
            </HoverBorderButton>
            <StyledButton className="sk:ml-2!" disabled={isDuplicate}>
              Save
            </StyledButton>
          </FlexRow>
        </JustifyBetweenRow>
      </form>
    </AppModal>
  );
}
